// Core
import { useEffect, useRef } from 'react';

// Icons
import { MdDoubleArrow, MdToken } from 'react-icons/md';
import { FaOilCan } from 'react-icons/fa';

// Chakra
import {
  Box,
  Button,
  Flex,
  Grid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
} from '@chakra-ui/react';

// Assets
import infoImage from '../../../assets/info.png';

const InfoModal = ({
  isOpen,
  onOpen,
  onClose,
}) => {
  const modalRef = useRef(null);

  const onCloseHandle = () => {
    localStorage.setItem('newUser', 'true');
    onClose();
  };

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  const isReturningUser =
    localStorage.getItem('newUser');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay />

      <ModalContent
        ref={modalRef}
        className="modal__simple modal__info"
        maxW={{ sm: '100%' }}
        pb="20px"
        overflow="hidden"
        position="relative"
        h={!isReturningUser ? '80%' : 'unset'}
      >
        {isReturningUser && (
          <ModalCloseButton zIndex="20" />
        )}

        {/* Neon glow */}
        <Box
          position="absolute"
          top="-90px"
          left="-80px"
          w="220px"
          h="220px"
          bg="rgba(255,49,87,0.09)"
          filter="blur(70px)"
          pointerEvents="none"
        />

        <Box
          position="absolute"
          top="-80px"
          right="-70px"
          w="210px"
          h="210px"
          bg="rgba(67,247,255,0.07)"
          filter="blur(70px)"
          pointerEvents="none"
        />

        {/* Header */}
        {isReturningUser && (
          <ModalHeader
            px="20px"
            pt="20px"
            pb="14px"
            position="relative"
            bg="rgba(0,0,0,0.16)"
            borderBottom="1px solid rgba(255,255,255,0.04)"
          >
            <Text
              fontSize="21px"
              fontWeight="900"
              letterSpacing="0.4px"
              color="rgba(255,255,255,0.82)"
            >
              DarianRace
            </Text>

            <Flex
              align="center"
              gap="7px"
              mt="4px"
            >
              <Box
                w="6px"
                h="6px"
                borderRadius="50%"
                bg="#FF3157"
                opacity="0.8"
                boxShadow="0 0 7px #FF3157"
              />

              <Text
                fontSize="11px"
                color="rgba(255,255,255,0.38)"
                fontWeight="600"
                textTransform="uppercase"
                letterSpacing="0.8px"
              >
                Game info & support
              </Text>
            </Flex>
          </ModalHeader>
        )}

        <ModalBody
          px="14px"
          pt="14px"
          pb="18px"
          overflowY="auto"
          css={{
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background:
                'rgba(255,49,87,0.22)',
              borderRadius: '10px',
            },
          }}
        >
          {/* Welcome */}
          <Box
            position="relative"
            overflow="hidden"
            minH={
              isReturningUser
                ? '420px'
                : '520px'
            }
            mt="24px"
            borderRadius="20px"
            border="1px solid rgba(255,255,255,0.07)"
            bg="#090D16"
            boxShadow="
              inset 0 0 30px rgba(0,0,0,0.3),
              0 0 28px rgba(0,0,0,0.2)
            "
          >
            {/* Background */}
            <Box
              position="absolute"
              inset="0"
              bg={`url(${infoImage}) no-repeat`}
              backgroundSize="cover"
              backgroundPosition="center bottom"
              opacity="0.42"
            />

            {/* Dark overlay */}
            <Box
              position="absolute"
              inset="0"
              bg="
                linear-gradient(
                  180deg,
                  rgba(5,8,15,0.68) 0%,
                  rgba(5,8,15,0.50) 36%,
                  rgba(5,8,15,0.82) 100%
                )
              "
            />

            {/* Color accents */}
            <Box
              position="absolute"
              top="-50px"
              left="-30px"
              w="180px"
              h="180px"
              bg="rgba(255,49,87,0.16)"
              filter="blur(65px)"
            />

            <Box
              position="absolute"
              bottom="-60px"
              right="-40px"
              w="190px"
              h="190px"
              bg="rgba(67,247,255,0.11)"
              filter="blur(70px)"
            />

            <Flex
              position="relative"
              zIndex="2"
              direction="column"
              align="center"
              textAlign="center"
              h="100%"
              minH={
                isReturningUser
                  ? '420px'
                  : '520px'
              }
              px="22px"
              pt={
                isReturningUser
                  ? '45px'
                  : '65px'
              }
              pb="20px"
            >
              {/* Token logo */}
              <Flex
                align="center"
                justify="center"
                w="74px"
                h="74px"
                borderRadius="22px"
                transform="rotate(45deg)"
                bg="rgba(255,49,87,0.10)"
                border="1px solid rgba(255,49,87,0.35)"
                boxShadow="
                  0 0 24px rgba(255,49,87,0.16),
                  inset 0 0 16px rgba(255,49,87,0.08)
                "
              >
                <MdToken
                  size={35}
                  color="#FF3157"
                  style={{
                    transform:
                      'rotate(-45deg)',
                    filter:
                      'drop-shadow(0 0 8px rgba(255,49,87,0.7))',
                  }}
                />
              </Flex>

              <Text
                mt="28px"
                maxW="330px"
                fontSize={{
                  base: '32px',
                  sm: '38px',
                }}
                lineHeight="1.05"
                fontWeight="950"
                letterSpacing="-1px"
                color="#F8FAFC"
              >
                Welcome to
                <Text
                  as="span"
                  display="block"
                  mt="5px"
                  color="#FFFFFF"
                  textShadow="
                    0 0 18px rgba(255,49,87,0.14)
                  "
                >
                  DARIANRACE
                </Text>
              </Text>

              <Text
                mt="20px"
                maxW="310px"
                fontSize="15px"
                lineHeight="22px"
                fontWeight="700"
                color="rgba(255,255,255,0.68)"
              >
                Collect tokens, unlock new
                cars and build your perfect
                night ride.
              </Text>

              <Text
                mt="12px"
                maxW="300px"
                fontSize="11px"
                lineHeight="17px"
                color="rgba(255,255,255,0.38)"
              >
                Cruise through the neon city,
                chase your best score and
                enjoy the road.
              </Text>

              {/* Start */}
              {!isReturningUser && (
                <Button
                  onClick={onCloseHandle}
                  mt="34px"
                  h="58px"
                  minW="190px"
                  px="25px"
                  borderRadius="17px"
                  bg="rgba(255,216,77,0.11)"
                  border="1px solid rgba(255,216,77,0.48)"
                  color="#FFD84D"
                  boxShadow="
                    0 0 22px rgba(255,184,0,0.12),
                    inset 0 0 16px rgba(255,216,77,0.04)
                  "
                  _hover={{
                    bg:
                      'rgba(255,216,77,0.17)',
                    transform:
                      'translateY(-1px)',
                  }}
                  _active={{
                    transform:
                      'translateY(1px)',
                  }}
                >
                  <Flex
                    align="center"
                    gap="6px"
                  >
                    <Text
                      fontSize="18px"
                      fontWeight="950"
                      fontStyle="italic"
                      textTransform="uppercase"
                      letterSpacing="1px"
                    >
                      Start
                    </Text>

                    <MdDoubleArrow
                      size={31}
                    />
                  </Flex>
                </Button>
              )}

              {/* Returning user small close CTA */}
              {isReturningUser && (
                <Button
                  onClick={onCloseHandle}
                  mt="30px"
                  h="44px"
                  px="20px"
                  borderRadius="13px"
                  variant="ghost"
                  bg="rgba(255,255,255,0.035)"
                  border="1px solid rgba(255,255,255,0.07)"
                  color="rgba(255,255,255,0.68)"
                  _hover={{
                    bg:
                      'rgba(255,255,255,0.07)',
                    color: '#fff',
                  }}
                >
                  <Text
                    fontSize="11px"
                    fontWeight="900"
                    textTransform="uppercase"
                    letterSpacing="0.8px"
                  >
                    Back to race
                  </Text>
                </Button>
              )}

              <Box
                mt="auto"
                pt="28px"
              >
                <Text
                  maxW="320px"
                  fontSize="8px"
                  lineHeight="12px"
                  color="rgba(255,255,255,0.19)"
                >
                  © 2026 DARIANRACE GAME.
                  All Rights Reserved.
                  Unauthorized reproduction,
                  distribution or modification
                  is prohibited.
                </Text>
              </Box>
            </Flex>
          </Box>

          {/* Support only after first start */}
          {isReturningUser && (
            <>
              <Flex
                mt="18px"
                mb="9px"
                px="5px"
                justify="space-between"
                align="center"
              >
                <Text
                  fontSize="11px"
                  color="rgba(255,255,255,0.38)"
                  fontWeight="800"
                  letterSpacing="0.7px"
                  textTransform="uppercase"
                >
                  Support
                </Text>

                <Text
                  fontSize="10px"
                  color="rgba(255,255,255,0.25)"
                >
                  Help DarianRace grow
                </Text>
              </Flex>

              <Grid
                templateColumns="58px 1fr"
                gap="12px"
                alignItems="start"
                p="15px"
                borderRadius="17px"
                bg="linear-gradient(135deg, rgba(67,247,255,0.05), rgba(255,255,255,0.02))"
                border="1px solid rgba(67,247,255,0.12)"
              >
                <Flex
                  align="center"
                  justify="center"
                  w="52px"
                  h="52px"
                  borderRadius="15px"
                  bg="rgba(67,247,255,0.06)"
                  border="1px solid rgba(67,247,255,0.17)"
                  color="#43F7FF"
                  boxShadow="
                    inset 0 0 14px rgba(67,247,255,0.05)
                  "
                >
                  <FaOilCan size={23} />
                </Flex>

                <Box>
                  <Text
                    fontSize="14px"
                    fontWeight="900"
                    color="#F5F8FB"
                  >
                    Support the Creators
                  </Text>

                  <Text
                    mt="5px"
                    fontSize="10px"
                    lineHeight="16px"
                    color="rgba(255,255,255,0.38)"
                  >
                    Enjoying the game? Your
                    support helps us build new
                    cars, features and future
                    updates.
                  </Text>

                  <Flex
                    mt="13px"
                    gap="8px"
                    wrap="wrap"
                  >
                    <Box
                      as="a"
                      href="https://t.me/tribute/app?startapp=dgiZ"
                    >
                      <Button
                        h="38px"
                        px="17px"
                        borderRadius="11px"
                        bg="rgba(67,247,255,0.11)"
                        border="1px solid rgba(67,247,255,0.35)"
                        color="#43F7FF"
                        _hover={{
                          bg:
                            'rgba(67,247,255,0.18)',
                        }}
                      >
                        <Text
                          fontSize="11px"
                          fontWeight="900"
                        >
                          Donate
                        </Text>
                      </Button>
                    </Box>

                    <Box
                      as="a"
                      href="https://darianrace-telegram-bot-game.tilda.ws"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        h="38px"
                        px="14px"
                        borderRadius="11px"
                        variant="ghost"
                        bg="rgba(255,255,255,0.025)"
                        border="1px solid rgba(255,255,255,0.06)"
                        color="rgba(255,255,255,0.55)"
                        _hover={{
                          bg:
                            'rgba(255,255,255,0.06)',
                          color: '#fff',
                        }}
                      >
                        <Text
                          fontSize="11px"
                          fontWeight="800"
                        >
                          Contact us
                        </Text>
                      </Button>
                    </Box>
                  </Flex>
                </Box>
              </Grid>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default InfoModal;